import React, { useCallback, useEffect, useRef } from 'react';

import {
  useListContext,
  usePermissions,
  useRedirect,
  useRefresh,
} from 'react-admin';

import {
  Box,
  Button,
  Tooltip,
} from '@mui/material';

import { isNil } from 'lodash';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';

import SortableTree from '@nosferatu500/react-sortable-tree';

import useRuleTreeState from '../../../../hooks/useRuleTreeState';

import hasAccess from '../../../../utilities/hasAccess';

import { RuleTypes } from '../../../../constants/ruleTypes';
import ruleViews from '../../../../constants/ruleView';
import { Rule } from '../../../../utilities/schemas/rules';
import actions, { Actions } from '../../../../constants/actions';
import { usePendingReviewBlocked } from '../../../../context/PendingReviewContext';

const PENDING_REVIEW_TOOLTIP = 'A review is pending — changes are blocked until it\'s resolved';

const TreeView = ({
  ruleType,
  isDraft,
  version,
}: {
  ruleType: RuleTypes;
  isDraft: boolean;
  version: string;
}) => {
  const { data, setPerPage } = useListContext();
  const { permissions } = usePermissions<Actions[]>();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const isPendingReviewBlocked = usePendingReviewBlocked();

  const {
    treeData,
    updateMove,
    setTreeData,
    removeNode,
  } = useRuleTreeState({ data });

  useEffect(() => {
    setPerPage(1000);
  }, [setPerPage]);

  // SortableTree only reads canDrop when it mounts, so a closure over isDraft and permissions
  // would stay stuck on their first values (e.g. a tree first shown on a published version)
  const canDropRef = useRef<(params: { node: Rule; nextParent: Rule }) => boolean>();
  canDropRef.current = ({ node, nextParent }) => {
    if (!isDraft) return false;
    if (!hasAccess(permissions, actions.RULE_UPDATE)) return false;
    if (isNil(nextParent)) return true;
    return node?.process === nextParent.process;
  };
  const canDrop = useCallback(
    (params: { node: Rule; nextParent: Rule }) => canDropRef.current!(params),
    [],
  );

  if (!data) return null;

  return (
    <Box
      height="100%"
      sx={{
        '& .rst__tree .rst__rowTitleWithSubtitle': {
          height: 'auto',
        },
      }}
    >
      <SortableTree
        getNodeKey={({ node }: { node: Rule }) => node.id}
        treeData={treeData.treeData}
        canDrag={() => isDraft}
        onChange={(item: Rule) => { if (isDraft) setTreeData({ treeData: item }); }}
        canDrop={canDrop}
        rowHeight={85}
        onMoveNode={
          ({
            node,
            nextParentNode,
          }: {
            node: Rule;
            nextParentNode: Rule;
          }) => {
            if (isDraft) {
              const triggeredBy = isNil(nextParentNode) ? undefined : nextParentNode?.id;
              updateMove({
                ...data.find((item) => item.id === node.id),
                triggeredBy,
              });
            }
          }
        }
        generateNodeProps={({
          node,
        }: {
          node: Rule;
          path: number[];
        }) => ({
          buttons: [
            <Box display="flex" alignItems="center">
              <>
                {(hasAccess(permissions, actions.RULE_CREATE) && isDraft) && (
                  <Tooltip title={isPendingReviewBlocked ? PENDING_REVIEW_TOOLTIP : ''}>
                    <span style={{ display: 'inline-flex' }}>
                      <Button
                        startIcon={<SubdirectoryArrowRightIcon />}
                        color="secondary"
                        disabled={isPendingReviewBlocked}
                        onClick={() => {
                          redirect(
                            'create',
                            'rules',
                            undefined,
                            undefined,
                            {
                              record: {
                                ruleType,
                                view: ruleViews.TREE,
                                parentRule: {
                                  process: node.process,
                                  id: node.id,
                                },
                                version,
                              },
                            },
                          );
                        }}
                      >
                        Add
                      </Button>
                    </span>
                  </Tooltip>
                )}
                {(hasAccess(permissions, actions.RULE_UPDATE)
                && hasAccess(permissions, actions.RULE_GET) && isDraft) && (
                  <Button
                    color="secondary"
                    startIcon={isPendingReviewBlocked ? <VisibilityIcon /> : <EditIcon />}
                    onClick={() => redirect(
                      'edit',
                      `rules/${node?.id}`,
                      'summary',
                      undefined,
                      {
                        record: {
                          ruleType,
                          view: ruleViews.TREE,
                          version,
                        },
                      },
                    )}
                  >
                    {isPendingReviewBlocked ? 'View' : 'Edit'}
                  </Button>
                )}
                {(hasAccess(permissions, actions.RULE_DELETE) && isDraft) && (
                  <Tooltip title={isPendingReviewBlocked ? PENDING_REVIEW_TOOLTIP : ''}>
                    <span style={{ display: 'inline-flex' }}>
                      <Button
                        color="secondary"
                        startIcon={<DeleteIcon />}
                        disabled={isPendingReviewBlocked}
                        onClick={() => {
                          removeNode(node);
                          refresh();
                        }}
                      >
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
              {(hasAccess(permissions, actions.RULE_GET) && !isDraft) && (
                <Button
                  color="secondary"
                  startIcon={<VisibilityIcon />}
                  onClick={() => redirect(
                    'edit',
                    `rules/${node?.id}`,
                    'summary',
                    undefined,
                    {
                      record: {
                        ruleType,
                        view: ruleViews.TREE,
                        version,
                      },
                    },
                  )}
                >
                  Show
                </Button>
              )}
            </Box>,
          ],
        })}
      />
    </Box>
  );
};

export default TreeView;
